
import { PenTool, Palette, Users, TrendingUp, ArrowRight, Sparkles } from "lucide-react";
import { IndustryPageLayout } from "@/components/IndustryPageLayout";
import { Card, CardContent } from "@/components/ui/card";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Tables } from "@/integrations/supabase/types";

type IndustryImage = Tables<'industry_images'>;

const CreativeAgenciesPage = () => {
  const [industryImages, setIndustryImages] = useState<IndustryImage[]>([]);

  useEffect(() => {
    const fetchIndustryImages = async () => {
      const { data, error } = await supabase
        .from('industry_images')
        .select('*')
        .eq('industry', 'creative-agencies')
        .order('display_order', { ascending: true });

      if (error) {
        console.error('Error fetching industry images:', error);
      } else {
        setIndustryImages(data || []);
      }
    };

    fetchIndustryImages();
  }, []);

  const industry = {
    title: "Creative Agencies",
    icon: PenTool,
    gradient: "from-cyan-500 to-blue-500",
    description: "Streamline creative workflows with AI-powered visual content generation that enhances team productivity and client satisfaction.",
    stats: "5x faster delivery"
  };

  const heroContent = (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <Card className="bg-white/5 backdrop-blur-md border-white/10">
        <CardContent className="p-6 text-center">
          <Palette className="w-8 h-8 text-cyan-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">Creative Freedom</h3>
          <p className="text-gray-300 text-sm">Unlimited concept exploration</p>
        </CardContent>
      </Card>
      <Card className="bg-white/5 backdrop-blur-md border-white/10">
        <CardContent className="p-6 text-center">
          <Sparkles className="w-8 h-8 text-cyan-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">Rapid Ideation</h3>
          <p className="text-gray-300 text-sm">From concept to visual in minutes</p>
        </CardContent>
      </Card>
      <Card className="bg-white/5 backdrop-blur-md border-white/10">
        <CardContent className="p-6 text-center">
          <TrendingUp className="w-8 h-8 text-cyan-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">Client Satisfaction</h3>
          <p className="text-gray-300 text-sm">Faster turnaround, better results</p>
        </CardContent>
      </Card>
    </div>
  );

  const imageGallery = (
    <div>
      <h3 className="text-2xl font-bold text-white text-center mb-8">AI-Generated Creative Examples</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {industryImages.length > 0 ? (
          industryImages.map((image) => (
            <Card key={image.id} className="bg-white/5 backdrop-blur-md border-white/10 overflow-hidden group hover:bg-white/10 transition-all duration-300">
              <div className="aspect-square relative overflow-hidden">
                <img 
                  src={image.image_url} 
                  alt={image.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
              <CardContent className="p-4">
                <p className="text-sm text-gray-300">{image.title}</p>
              </CardContent>
            </Card>
          ))
        ) : (
          <div className="col-span-3 text-center text-gray-400 py-12">
            <PenTool className="w-12 h-12 mx-auto mb-4" />
            <p className="text-white">No creative agency examples available yet</p>
            <p className="text-sm text-gray-400 mt-2">Images will be added soon</p>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <IndustryPageLayout
      industry={industry}
      heroContent={heroContent}
      mainContent={
        <div className="prose prose-invert max-w-none">
          <h2 className="text-3xl font-bold text-white mb-6">Revolutionizing Creative Workflows</h2>
          
          <div className="text-gray-300 space-y-6 text-lg leading-relaxed">
            <p>
              Creative agencies face constant pressure to deliver innovative, high-quality visuals under tight deadlines. AI-generated imagery is transforming how agencies approach creative projects, enabling faster ideation and execution while maintaining creative excellence.
            </p>
            
            <h3 className="text-2xl font-semibold text-white mt-8 mb-4">The Agency Challenge</h3>
            <p>
              Balancing creativity with productivity, managing multiple client projects, and meeting demanding timelines while maintaining quality standards has always been the creative agency dilemma.
            </p>
            
            <h3 className="text-2xl font-semibold text-white mt-8 mb-4">AI-Enhanced Creative Process</h3>
            <ul className="list-disc list-inside space-y-3 text-gray-300">
              <li><strong className="text-white">Concept Visualization:</strong> Rapidly prototype and present ideas to clients</li>
              <li><strong className="text-white">Style Exploration:</strong> Test different artistic directions efficiently</li>
              <li><strong className="text-white">Asset Generation:</strong> Create supporting visuals and variations</li>
              <li><strong className="text-white">Client Presentations:</strong> Enhance pitches with compelling visuals</li>
              <li><strong className="text-white">Creative Iteration:</strong> Quickly refine concepts based on feedback</li>
            </ul>
            
            <h3 className="text-2xl font-semibold text-white mt-8 mb-4">Agency Success Stories</h3>
            <p>
              Creative agencies using AI report 5x faster project delivery, 60% more concepts explored per project, and 40% higher client satisfaction scores. The technology has become an essential tool for modern creative workflows.
            </p>
            
            <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-lg p-6 mt-8">
              <h4 className="text-xl font-semibold text-cyan-400 mb-3 flex items-center">
                <PenTool className="w-5 h-5 mr-2" />
                Enhance Your Creative Process
              </h4>
              <p className="text-gray-300">
                Streamline your creative workflows with AI-powered visual generation that amplifies your team's creativity and productivity.
              </p>
            </div>
          </div>
        </div>
      }
      imageGallery={imageGallery}
    />
  );
};

export default CreativeAgenciesPage;
